import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react'

const BusinessIntroduction: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set())
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observers: IntersectionObserver[] = []

    sectionRefs.current.forEach((ref, index) => {
      if (!ref) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index))
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px',
        }
      )

      observer.observe(ref)
      observers.push(observer)
    })

    return () => {
      observers.forEach((observer) => observer.disconnect())
    }
  }, [])

  return (
    <DefaultLayout>
      <Box>
        <VStack spacing={0} align="stretch">
          {/* 왼쪽 이미지 - 오른쪽 설명: 100평 쉼터 */}
          <Container maxW="container.xl" py={10}>
          <Box
            as="section"
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[0] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                style={{
                  opacity: visibleSections.has(0) ? 1 : 0,
                  transform: visibleSections.has(0) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/1.mp4"
                />
              </Box>
              <VStack
                align="stretch"
                spacing={6}
                style={{
                  opacity: visibleSections.has(0) ? 1 : 0,
                  transform: visibleSections.has(0) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="brand.600"
                  >
                    100평 쉼터
                  </Heading>
                  <Text fontSize="2xl" fontWeight="bold" mb={4} color="gray.700">
                    100배의 여유를 드리는 공간
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    펜텔의 여유를 체험해보세요! 도심 한복판에서도 100평의 넓은 공간을 만나보실 수 있습니다.
                    넓은 정원과 텃밭, 그리고 프라이빗한 공간이 어우러진 별장 같은 경험을 제공합니다.
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="tall">
                    단순한 숙박 공간을 넘어, 가족과 함께하는 소중한 시간을 담을 수 있는 공간입니다.
                    아이들이 마음껏 뛰어놀 수 있는 정원, 반려동물과 함께하는 여유로운 하루,
                    그리고 당신만의 힐링 타임을 위한 프라이빗 공간까지.
                  </Text>
                </Box>
              </VStack>
            </SimpleGrid>
          </Box>

          {/* 왼쪽 설명 - 오른쪽 이미지: 뉴노멀 시대 */}
          <Box
            as="section"
            bg="gray.50"
            borderRadius="xl"
            p={{ base: 6, md: 12 }}
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[1] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <VStack
                align="stretch"
                spacing={6}
                order={{ base: 2, md: 1 }}
                style={{
                  opacity: visibleSections.has(1) ? 1 : 0,
                  transform: visibleSections.has(1) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="gray.700"
                  >
                    뉴노멀 시대에 떠오르는 HOME
                  </Heading>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={6}>
                    팬데믹 이후 우리의 삶은 완전히 바뀌었습니다. 집은 이제 단순한 거주 공간이 아닌,
                    일과 휴식, 건강과 안전이 모두 어우러지는 종합적인 라이프스타일 공간이 되었습니다.
                  </Text>
                </Box>
                <SimpleGrid columns={2} spacing={4}>
                  {[
                    { letter: 'H', title: 'health care', desc: '건강과 방역에 대한 새로운 인식' },
                    { letter: 'O', title: 'on-line', desc: '디지털 경제의 핵심' },
                    { letter: 'M', title: 'manless', desc: '검증된 무인화 시스템' },
                    { letter: 'E', title: 'economy at home', desc: '형성된 홈코노미' },
                  ].map((item, idx) => (
                    <Box
                      key={idx}
                      bg="white"
                      p={4}
                      borderRadius="lg"
                      boxShadow="sm"
                      textAlign="center"
                      style={{
                        opacity: visibleSections.has(1) ? 1 : 0,
                        transform: visibleSections.has(1) ? 'translateY(0)' : 'translateY(20px)',
                        transition: `opacity 0.6s ease-out ${0.6 + idx * 0.1}s, transform 0.6s ease-out ${0.6 + idx * 0.1}s`,
                      }}
                      _hover={{
                        transform: 'translateY(-5px)',
                        boxShadow: 'lg',
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      <Heading as="h3" size="md" color="brand.600" mb={2}>
                        {item.letter}
                      </Heading>
                      <Text fontSize="xs" fontWeight="bold" mb={1} color="gray.700">
                        {item.title}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {item.desc}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                order={{ base: 1, md: 2 }}
                style={{
                  opacity: visibleSections.has(1) ? 1 : 0,
                  transform: visibleSections.has(1) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/2.mp4"
                />
              </Box>
            </SimpleGrid>
          </Box>

          {/* 왼쪽 이미지 - 오른쪽 설명: Pension + Hotel = PENTEL */}
          <Box
            as="section"
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[2] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                style={{
                  opacity: visibleSections.has(2) ? 1 : 0,
                  transform: visibleSections.has(2) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/3.mp4"
                />
              </Box>
              <VStack
                align="stretch"
                spacing={6}
                style={{
                  opacity: visibleSections.has(2) ? 1 : 0,
                  transform: visibleSections.has(2) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="brand.600"
                  >
                    Pension + Hotel = PENTEL
                  </Heading>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    펜션의 독립성과 호텔의 편의성을 결합한 숙박/주거 복합형 주택입니다.
                    펜션처럼 프라이빗하고 넓은 공간을 제공하면서도, 호텔처럼 편리한 서비스와
                    관리 시스템을 갖추고 있습니다.
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    단독 주택의 자유로움과 호텔의 안전함을 동시에 경험할 수 있습니다.
                    무인 체크인/아웃 시스템으로 프라이버시를 보장하고,
                    필요할 때만 호출할 수 있는 맞춤형 서비스를 제공합니다.
                  </Text>
                  <Box
                    bg="brand.50"
                    p={4}
                    borderRadius="lg"
                    borderLeft="4px solid"
                    borderColor="brand.500"
                  >
                    <Text fontSize="sm" color="gray.700" fontStyle="italic">
                      "펜션의 로망과 호텔의 편의를 한 곳에서"
                    </Text>
                  </Box>
                </Box>
              </VStack>
            </SimpleGrid>
          </Box>

          {/* 왼쪽 설명 - 오른쪽 이미지: 쉼터 */}
          <Box
            as="section"
            bg="brand.50"
            borderRadius="xl"
            p={{ base: 6, md: 12 }}
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[3] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <VStack
                align="stretch"
                spacing={6}
                order={{ base: 2, md: 1 }}
                style={{
                  opacity: visibleSections.has(3) ? 1 : 0,
                  transform: visibleSections.has(3) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="gray.700"
                  >
                    쉼터
                  </Heading>
                  <Text fontSize="lg" color="gray.600" mb={4} lineHeight="tall">
                    최초로 공개되는 별장 같은 펜텔
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    100평 공간이 제공됩니다. 이 넓은 공간은 단순한 숫자가 아닙니다.
                    가족이 함께 모여 웃고, 이야기하고, 추억을 만들 수 있는 공간입니다.
                  </Text>
                  <Box
                    bg="white"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    boxShadow="sm"
                  >
                    <Text fontSize="lg" fontStyle="italic" color="brand.600" fontWeight="bold">
                      "이게 15평이야? 저택인 줄"
                    </Text>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      - 펜텔을 방문한 고객의 후기
                    </Text>
                  </Box>
                </Box>
              </VStack>
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                order={{ base: 1, md: 2 }}
                style={{
                  opacity: visibleSections.has(3) ? 1 : 0,
                  transform: visibleSections.has(3) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/4.mp4"
                />
              </Box>
            </SimpleGrid>
          </Box>

          {/* 왼쪽 이미지 - 오른쪽 설명: 로망과 현실 */}
          <Box
            as="section"
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[4] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                style={{
                  opacity: visibleSections.has(4) ? 1 : 0,
                  transform: visibleSections.has(4) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/5.mp4"
                />
              </Box>
              <VStack
                align="stretch"
                spacing={6}
                style={{
                  opacity: visibleSections.has(4) ? 1 : 0,
                  transform: visibleSections.has(4) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="gray.700"
                  >
                    로망을 현실로
                  </Heading>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    정원과 텃밭이 딸린 단독주택, 그곳에서 뛰노는 아이와 반려동물의 행복한 모습은
                    성공한 중산층의 로망입니다. MZ세대들에게도 도심 속 전원주택은 로망입니다.
                  </Text>
                  <Text fontSize="md" color="gray.600" lineHeight="tall" mb={4}>
                    하지만 이제 로망을 넘어, 정서적 안정(힐링)과 정신적 건강(웰빙)을 제공하는
                    공간이 되었습니다. 행복해지고 싶은 인간의 본질적 욕망을 실현할 수 있는 곳,
                    바로 펜텔입니다.
                  </Text>
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="lg"
                  >
                    <Text fontSize="md" fontWeight="bold" color="brand.600" mb={2}>
                      금전적 부담없이 모두가 만족하는
                    </Text>
                    <Text fontSize="md" fontWeight="bold" color="brand.600">
                      새로운 미래 주거문화를 제공할 기업
                    </Text>
                  </Box>
                </Box>
              </VStack>
            </SimpleGrid>
          </Box>

          {/* 왼쪽 설명 - 오른쪽 이미지: 미래 비전 */}
          <Box
            as="section"
            bg="gray.900"
            color="white"
            borderRadius="xl"
            p={{ base: 6, md: 12 }}
            py={12}
            ref={(el: HTMLDivElement | null) => (sectionRefs.current[5] = el)}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={10}
              alignItems="center"
            >
              <VStack
                align="stretch"
                spacing={6}
                order={{ base: 2, md: 1 }}
                style={{
                  opacity: visibleSections.has(5) ? 1 : 0,
                  transform: visibleSections.has(5) ? 'translateX(0)' : 'translateX(-50px)',
                  transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s',
                }}
              >
                <Box>
                  <Heading
                    as="h2"
                    size={isMobile ? 'lg' : 'xl'}
                    mb={4}
                    color="white"
                  >
                    새로운 라이프스타일의 시작
                  </Heading>
                  <Text fontSize="md" color="gray.300" lineHeight="tall" mb={4}>
                    펜텔은 단순한 숙박 시설이 아닙니다. 도심 속에서도 자연과 함께 살아가는
                    새로운 라이프스타일을 제안합니다. 주말 여행지가 아닌, 일상 속 힐링 공간으로
                    여러분의 삶에 새로운 여유를 더합니다.
                  </Text>
                  <Text fontSize="md" color="gray.300" lineHeight="tall">
                    스마트 홈 시스템과 무인 관리 시스템을 통해 프라이버시를 보장하면서도,
                    필요할 때 언제든지 전문 서비스를 받을 수 있습니다.
                    이제 집은 더 이상 갇힌 공간이 아닌, 자유롭고 여유로운 삶의 터전이 됩니다.
                  </Text>
                </Box>
              </VStack>
              <Box
                w="100%"
                h={{ base: '300px', md: '500px' }}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                order={{ base: 1, md: 2 }}
                style={{
                  opacity: visibleSections.has(5) ? 1 : 0,
                  transform: visibleSections.has(5) ? 'translateX(0)' : 'translateX(50px)',
                  transition: 'opacity 0.8s ease-out 0.4s, transform 0.8s ease-out 0.4s',
                }}
                _hover={{
                  transform: 'scale(1.02)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                <Box
                  as="video"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  src="/videos/6.mp4"
                />
              </Box>
            </SimpleGrid>
          </Box>

          </Container>
        </VStack>
      </Box>
    </DefaultLayout>
  )
}

export default BusinessIntroduction

